import { NestedDeal, TODO } from "@revelate/types";
import pLimit, { type LimitFunction } from "p-limit";
import { supabaseClient } from "./client";
import {
  DEAL_FETCH_MAX_ATTEMPTS,
  shouldRetryDealFetchError,
} from "./dealFetchRetry";
import {
  attachPipelineRiskEmailProjection,
  PipelineRiskEmailProjectionRow,
} from "./pipelineRiskProjection";
import {
  SALES_ECONOMICS_ACTIVITY_CONCURRENCY,
  SALES_ECONOMICS_EMAIL_COLUMNS,
  SALES_ECONOMICS_MEETING_COLUMNS,
  normalizeSalesEconomicsProviderData,
} from "./salesEconomicsTransport";

// TODO: Map the output TS type for each
export type TableType =
  | "companies"
  | "plans"
  | "quotas"
  | "currencies"
  | "exchange_rates"
  | "deals"
  | "tokens"
  | "users"
  | "teams";

// TODO: Some possibly undocumented types are coming out of the current queries
export const getTableColumns = (table: TableType) => {
  switch (table) {
    // SignedInCompany
    case "companies":
      return `
        *,
        teams(*, users(*)),
        plans(*),
        quotas(*),
        users!public_profiles_company_id_fkey(*, currency:currencies!users_currency_id_fkey(*)),
        default_currency:currencies!companies_default_currency_id_fkey(*, exchange_rates!exchange_rates_to_currency_id_fkey(*))
      `;

    // NestedPlan
    case "plans":
      return `
        *,
        teams(*, users(*)),
        users(*),
        accelerators(*, conditions(*))
      `;

    // NestedQuota
    case "quotas":
      return `
        *,
        currency:currencies(*),
        users(*),
        goals(*, users(*))
      `;

    // NestedCurrency
    case "currencies":
      return `
        *,
        exchange_rates!exchange_rates_from_currency_id_fkey(*)
      `;

    // Pick<NestedToken, ... >
    // TODO: Looks like an ad-hoc type. Investigate.
    case "tokens":
      return "id, provider, created_at, updated_at, is_expired, allow_update";

    // NestedExchangeRate
    case "exchange_rates":
      return "*";

    // NestedDeal
    case "deals":
      return `
        *,
        users:user_deals(user_id, role, data:users(*))
      `;

    // User & { currency: Currency }
    // TODO: Looks like an ad-hoc type. Investigate.
    case "users":
      return `
        *,
        currency:currencies!users_currency_id_fkey(*)
      `;

    // NestedTeam
    case "teams":
      return `
        *,
        users(*)
      `;

    // Full table
    default:
      return "*";
  }
};

export const getAll = async (
  table: TableType,
  companyId: string | null,
  activeOnly = true,
  userRole?: string
): Promise<TODO[]> => {
  let startTime: number | undefined = undefined;

  const query = supabaseClient
    .from(table)
    .select(getTableColumns(table))
    .eq("company_id", Number(companyId));

  if (table === "users") {
    query.neq("role", "super_admin");
    if (activeOnly) {
      query.eq("is_active", true);
    }
  }

  if (table === "quotas" && userRole !== "super_admin") {
    query.eq("is_hidden", false);
  }

  if (table === "plans") {
    query.order("name", { ascending: true });
  }

  if (table === "teams") {
    query.order("name", { ascending: true });
  }

  if (table === "deals") {
    // FIXME: This arbitrary limit means we are fetching an incomplete list
    // and then calculating off that incorrect list
    // Investigate possible request timeout issues when fetched without limit
    // Also consider passing the daterange here when deals are fetched
    // so we can selectively fetch the right deals and get a better result even with a limit
    query.limit(1_000);

    startTime = performance.now();
  }

  const { data, error } = await query.returns<TODO[]>();

  if (table === "deals" && startTime !== undefined) {
    const endTime = performance.now();
    console.warn(
      `Fetching ${data?.length ?? 0} deals took ${(endTime - startTime).toFixed(0)} ms`
    );
  }

  if (error) {
    throw error;
  }

  return data;
};

export const get = async (
  table: TableType,
  id: string | null,
  activeOnly = true
): Promise<TODO | null> => {
  if (!id) {
    return null;
  }

  const query = supabaseClient
    .from(table)
    .select(getTableColumns(table))
    .eq("id", Number(id));

  if (table === "companies") {
    query.neq("users.role", "super_admin");
    if (activeOnly) {
      query.eq("users.is_active", true);
    }
  }

  const { data, error } = await query.returns<TODO[]>().maybeSingle();
  if (error) {
    throw error;
  }
  return data;
};

type GetDealsOpts = {
  activityProjection?: "full" | "growth_efficiency" | "pipeline_risk";
  dateRange?: { from: string, to?: string },
  onlyOpen?: boolean;
  onlyClosedOutcomes?: boolean;
  onlyWon?: boolean;
  provider?: string;
  nonZeroValue?: boolean;
  includeEmails?: boolean;
  includeMeetings?: boolean;
  includeActivitySyncStatus?: boolean;
  filterByCreateDateInsteadOfCloseDate?: boolean;
};

const defaultGetDealOpts: GetDealsOpts = {
  activityProjection: "full",
  dateRange: undefined,
  onlyOpen: false,
  onlyClosedOutcomes: false,
  onlyWon: false,
  provider: "hubspot", // Currently supports only single provider for better indexing
  nonZeroValue: false,
  includeEmails: false,
  includeMeetings: false,
  includeActivitySyncStatus: false,
  filterByCreateDateInsteadOfCloseDate: false,
};

const PAGE_SIZE = 1_000;
// const PAGE_SIZE = 250;
const MAX_PAGES = 50;

const ACTIVITY_DEAL_CHUNK_SIZE = 200;

type GrowthEfficiencyEmailRow = {
  provider: string;
  provider_id: string;
  deal_id: number;
  sender_email?: string | null;
  timestamp: string;
};

type GrowthEfficiencyMeetingRow = {
  provider: string;
  provider_id: string;
  attendee_emails?: string | null;
  deal_id: number;
  ended_at: string | null;
  seller_participant_count?: string | null;
  started_at: string | null;
};

type GrowthEfficiencyActivityStatusRow = {
  activity_type: string;
  deal_id: number;
  record_count: number | null;
  status: string;
};

function chunkDealIds(dealIds: number[]) {
  const chunks: number[][] = [];
  for (let index = 0; index < dealIds.length; index += ACTIVITY_DEAL_CHUNK_SIZE) {
    chunks.push(dealIds.slice(index, index + ACTIVITY_DEAL_CHUNK_SIZE));
  }
  return chunks;
}

async function getProjectedActivityRows<TRow extends { deal_id: number }>(
  table: "deal_activity_sync_status" | "deal_emails" | "deal_meetings",
  columns: string,
  dealIds: number[],
  secondaryOrder: string,
  runTask?: LimitFunction,
) {
  const fetchChunk = async (ids: number[]) => {
    const chunkRows: TRow[] = [];
    let page = 0;
    while (page < MAX_PAGES) {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await supabaseClient
        .from(table)
        .select(columns)
        .in("deal_id", ids)
        .order("deal_id", { ascending: true })
        .order(secondaryOrder, { ascending: true })
        .range(from, to)
        .returns<TRow[]>();

      if (error) throw error;
      chunkRows.push(...data);
      if (data.length < PAGE_SIZE) break;
      page++;
    }
    return chunkRows;
  };

  const chunks = chunkDealIds(dealIds);
  if (!runTask) {
    const rows: TRow[] = [];
    for (const ids of chunks) {
      rows.push(...await fetchChunk(ids));
    }
    return rows;
  }

  const rowsByChunk = await Promise.all(
    chunks.map(ids => runTask(() => fetchChunk(ids))),
  );
  return rowsByChunk.flat();
}

function groupGrowthEfficiencyActivityRows<TRow extends { deal_id: number }>(
  rows: TRow[],
) {
  const grouped = new Map<number, TRow[]>();
  for (const row of rows) {
    const dealRows = grouped.get(row.deal_id) ?? [];
    dealRows.push(row);
    grouped.set(row.deal_id, dealRows);
  }
  return grouped;
}

async function attachGrowthEfficiencyActivity(
  deals: NestedDeal[],
  opts: GetDealsOpts,
) {
  const dealIds = deals.map(deal => deal.id);
  if (!dealIds.length) return deals;
  const runActivityTask = pLimit(SALES_ECONOMICS_ACTIVITY_CONCURRENCY);

  const [emailRows, meetingRows, statusRows] = await Promise.all([
    opts.includeEmails
      ? getProjectedActivityRows<GrowthEfficiencyEmailRow>(
          "deal_emails",
          SALES_ECONOMICS_EMAIL_COLUMNS,
          dealIds,
          "id",
          runActivityTask,
        )
      : Promise.resolve([]),
    opts.includeMeetings
      ? getProjectedActivityRows<GrowthEfficiencyMeetingRow>(
          "deal_meetings",
          SALES_ECONOMICS_MEETING_COLUMNS,
          dealIds,
          "id",
          runActivityTask,
        )
      : Promise.resolve([]),
    opts.includeActivitySyncStatus
      ? getProjectedActivityRows<GrowthEfficiencyActivityStatusRow>(
          "deal_activity_sync_status",
          "deal_id,activity_type,status,record_count",
          dealIds,
          "activity_type",
          runActivityTask,
        )
      : Promise.resolve([]),
  ]);
  const emailsByDeal = groupGrowthEfficiencyActivityRows(emailRows);
  const meetingsByDeal = groupGrowthEfficiencyActivityRows(meetingRows);
  const statusesByDeal = groupGrowthEfficiencyActivityRows(statusRows);

  return deals.map(deal => ({
    ...deal,
    ...(opts.includeEmails ? { emails: emailsByDeal.get(deal.id) ?? [] } : {}),
    ...(opts.includeMeetings ? { meetings: meetingsByDeal.get(deal.id) ?? [] } : {}),
    ...(opts.includeActivitySyncStatus
      ? { activity_sync_status: statusesByDeal.get(deal.id) ?? [] }
      : {}),
  })) as NestedDeal[];
}

export async function getPipelineRiskDealsWithEmails(deals: NestedDeal[]) {
  const dealIds = deals.map(deal => deal.id);
  if (!dealIds.length) return deals;

  const emailRows = await getProjectedActivityRows<PipelineRiskEmailProjectionRow>(
    "deal_emails",
    "id,deal_id,timestamp,semantic_analysis,analysed_at,created_at,provider,provider_data,provider_id,sender_email:data->from->>email",
    dealIds,
    "id",
  );
  return attachPipelineRiskEmailProjection(deals, emailRows);
}

export async function getDeals(
  companyId: string | null,
  opts?: GetDealsOpts,
): Promise<NestedDeal[]> {
  if (companyId === null) return [];

  const fetchOpts = { ...defaultGetDealOpts, ...opts };

  if (fetchOpts.activityProjection === "growth_efficiency") {
    const startedAt = performance.now();
    const projectedDeals: NestedDeal[] = [];
    for (let page = 0; page < MAX_PAGES; page += 1) {
      const from = page * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, error } = await supabaseClient.rpc(
        "get_sales_economics_deals",
        {
          target_company_id: Number(companyId),
          range_from: fetchOpts.dateRange?.from
            ? new Date(fetchOpts.dateRange.from).toISOString()
            : null,
          range_to: fetchOpts.dateRange?.to
            ? new Date(fetchOpts.dateRange.to).toISOString()
            : null,
          only_open: fetchOpts.onlyOpen,
          only_won: fetchOpts.onlyWon,
          only_closed_outcomes: fetchOpts.onlyClosedOutcomes,
          non_zero_value: fetchOpts.nonZeroValue,
          filter_by_created_at: fetchOpts.filterByCreateDateInsteadOfCloseDate,
          result_offset: from,
          result_limit: PAGE_SIZE,
        },
      );
      if (error) throw error;
      const rows = (data ?? []).map((row: Record<string, unknown>) => ({
        ...row,
        provider_data: normalizeSalesEconomicsProviderData(row.provider_data),
      })) as unknown as NestedDeal[];
      projectedDeals.push(...rows);
      if (rows.length < PAGE_SIZE) break;
    }
    console.log(
      `Fetched ${projectedDeals.length} compact Sales Economics deals in ${(
        performance.now() - startedAt
      ).toFixed(0)} ms`,
    );
    return attachGrowthEfficiencyActivity(projectedDeals, fetchOpts);
  }

  let page = 0;
  const allDeals: NestedDeal[] = [];

  const startTime = performance.now();

  const pipelineRiskProjection = fetchOpts.activityProjection
    === "pipeline_risk";
  const hasSeparateActivityProjection = pipelineRiskProjection;
  const relations = hasSeparateActivityProjection ? [] : [
    opts?.includeEmails
      ? "emails:deal_emails(*)"
      : null,
    opts?.includeMeetings
      ? "meetings:deal_meetings(*)"
      : null,
    opts?.includeActivitySyncStatus
      ? "activity_sync_status:deal_activity_sync_status(*)"
      : null,
  ].filter(Boolean);
  const dealColumns = getTableColumns("deals");
  const columns = [dealColumns, ...relations].join(",");

  const filterDateColumn = fetchOpts.filterByCreateDateInsteadOfCloseDate
    ? "created_at"
    : "closed_at";

  let query = supabaseClient
    .from("deals")
    .select(columns)
    .eq("company_id", Number(companyId));

  if (fetchOpts.dateRange?.from && fetchOpts.dateRange?.to) {
    query = query
      .gte(filterDateColumn, new Date(fetchOpts.dateRange.from).toISOString())
      .lte(filterDateColumn, new Date(fetchOpts.dateRange.to).toISOString());
  }

  if (fetchOpts.onlyOpen) {
    query = query
      .neq("is_closed", true)
      .neq("is_won", true)
      .neq("is_lost", true)

    // More optimized version, if needed
    // query = query
    //   .neq("is_closed", false)
  } else if (fetchOpts.onlyWon) {
    query = query
      .eq("is_closed", true)
      .eq("is_won", true)

    // More optimized version, if needed
    // query = query
    //   .eq("is_won", true)
  } else if (fetchOpts.onlyClosedOutcomes) {
    query = query.or("is_won.eq.true,is_lost.eq.true");
  }

  query = query
    .eq("provider", fetchOpts.provider)

  if (fetchOpts.nonZeroValue) {
    query = query
      .neq("value", 0)
  }

  query = query
    .order("id", { ascending: false });

  // Fetch paginated
  for (;;) {
    console.log(`Fetching deals for company ${companyId}`);

    const from = page * PAGE_SIZE;
    const to = from + PAGE_SIZE - 1;

    query = query.range(from, to);

    let fetchedDeals: NestedDeal[] = [];
    let attempt = 0;
    let fetchSuccess = false;

    while (!fetchSuccess && attempt < DEAL_FETCH_MAX_ATTEMPTS) {
      console.log(`Page ${page}, attempt ${attempt + 1}`);

      const { data, error } = await query.returns<NestedDeal[]>();

      if (error) {
        console.error(`Attempt ${attempt + 1} failed`);

        if (!shouldRetryDealFetchError(error)) throw error;
        attempt++;

        if (attempt >= DEAL_FETCH_MAX_ATTEMPTS) {
          throw new Error(`Failed ${attempt} attempts. Exiting.`);
        }
      } else {
        fetchSuccess = true;
        fetchedDeals = [...data] as NestedDeal[];
      }
    }

    if (fetchedDeals.length === 0) break;

    allDeals.push(...fetchedDeals);

    if (fetchedDeals.length < PAGE_SIZE) break;

    page++;

    if (page >= MAX_PAGES) break;
  }

  const endTime = performance.now();

  console.log(
    `Fetched ${allDeals.length ?? 0} deals in ${(endTime - startTime).toFixed(0)} ms`
  );

  if (pipelineRiskProjection && fetchOpts.includeEmails) {
    return getPipelineRiskDealsWithEmails(allDeals);
  }
  return allDeals;
}

export async function getDeal(
  companyId: string | null,
  dealId: number | null,
): Promise<NestedDeal | null> {
  if (companyId === null || dealId === null) return null;

  const columns = getTableColumns("deals");

  const query = supabaseClient
    .from("deals")
    .select(columns)
    .eq("company_id", Number(companyId))
    .eq("id", dealId);

  const { data: deal, error } = await query.returns<NestedDeal[]>().maybeSingle();

  if (error) console.error(error);

  return deal;
}

export async function hasHubspotToken(
  companyId: string | null,
): Promise<boolean> {
  if (companyId === null) return false;

  const { data, error } = await supabaseClient
    .from("tokens")
    .select("id")
    .eq("company_id", Number(companyId))
    .eq("provider", "hubspot")
    .maybeSingle();

  if (error) return false;
  return data !== null;
}
